import { OverlayEventDetail } from '@ionic/core';
import { AlertController, PopoverController } from '@ionic/angular';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { DataTableComponent } from '@shared/components';
import { Events } from '@services/events/events.service';
// CVX-6069: import directly, not via the components barrel — the barrel exports
// folders-list before folder-header, so a barrel import resolves to undefined at
// module-eval and crashes the karma test bundle (circular dependency).
import { FolderHeaderComponent } from '@modules/management/modules/folders/components/folder-header/folder-header.component';
import { ITreeViewModalConfig, TreeViewNode, TreeViewNodeIdType } from '@shared/modules/tree-viewer/models';
import { FoldersService } from '@modules/management/modules/folders/services/folders.service';
import { FolderPickerService } from '@modules/management/modules/folders/services/folder-picker.service';
import { FolderPanelSelectionType, IFolderInput, IPanelButton, IPanelConfig, IFolder, FolderPickerConfig } from './../../model/folders.interfaces';
import { FoldersDataService, DataTableOptions, TableService, LoadingService, NativeElementService, ToastService } from '@services';
import { ViewModalTreePopupComponent } from '@shared/modules/tree-viewer/view-modals/view-modal-popup/view-modal-popup.component';

import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, each, filter, get, includes, map, orderBy, some, split, values } from 'lodash';

@Component({
  selector: 'app-folders-list',
  templateUrl: './folders-list.component.html',
  styleUrls: ['./folders-list.component.scss'],
})
export class FoldersListComponent implements OnInit, OnDestroy {
  @ViewChild(DataTableComponent) dataTableComponent: DataTableComponent;
  @ViewChild(FolderHeaderComponent) folderHeaderComponent: FolderHeaderComponent;
  @Input() foldersConfig: IFolderInput;

  public folders: IFolder[];
  public allFoldersList: IFolder[];
  public tableDataReady: boolean = false;
  public currentFolderId: number = 0;
  public panelConfig: IPanelConfig;
  public searchWord: string;
  public foldersTableID = 'foldersTableID';
  public filteredFoldersList: IFolder[] = [];
  public mainDataTableOptions: DataTableOptions;
  public emptyMessage: string;
  public hasActiveSelection: boolean = false;
  public showBulkImport: boolean = false;
  public updateSearchSelection = (ids: number[], type: FolderPanelSelectionType | null): void => {
    if (!ids.length || !type) {
      this.setActiveSelection([]);
      return;
    }

    this.setActiveSelection(ids, type);
  };
  private itemButtons: IPanelButton[] = [];
  private folderButtons: IPanelButton[] = [];
  private ignoreItemSelectionChanges = false;
  private ignoreFoldersUpdate = false;
  private preservingRefresh = false;
  private preservedItemIds: number[] = [];
  private defaultPanelSubTitle: string;

  public dataTableOptions: DataTableOptions = {
    disableAutoResizeHandler: true,
    data: [],
    paging: false,
    info: false,
    scrollX: true,
    retrieve: true,
    stateSave: true,
    searching: true,
    autoWidth: false,
    order: [[1, 'asc']],
    enableSelectAll: true,
    select: {
      style: 'multi',
      selector: 'td:first-child'
    },
    columnDefs: [
      {
        orderable: false,
        className: 'select-checkbox select-all no-vis',
        targets: 0,
        width: '20px'
      },
    ],
    language: {},
    dom: 'Bftip',
    buttons: [
      {
        extend: 'colvis',
        columns: ':not(.no-vis)',
        text: this.translate.instant('MGMT_LIST.Select_Columns'),
        className: 'button-styled hidden',
        collectionLayout: 'three-column'
      }
    ]
  };

  public searchModalTableOptions: DataTableOptions;

  private foldersUpdateListener = () => {
    if (this.ignoreFoldersUpdate) {
      return;
    }

    // Snapshot the folder selection BEFORE getFolderData() rebuilds this.folders,
    // so an SSE-triggered ccs:foldersUpdate auto-refresh does not silently drop it.
    const previouslySelected = new Set<number>(
      this.panelConfig?.selectionType === FolderPanelSelectionType.Folders
        ? this.panelConfig?.selectedIds || []
        : []
    );

    this.getFolderData();
    this.filteredFoldersList = this.folders;

    if (!previouslySelected.size) {
      return;
    }

    // Re-apply selected=true on BOTH arrays for IDs still present after the
    // reload (mirrors clearFolderSelection's dual-array iteration).
    each(this.folders, (folder: IFolder) => {
      if (previouslySelected.has(folder.folderID)) {
        folder.selected = true;
      }
    });
    each(this.filteredFoldersList, (folder: IFolder) => {
      if (previouslySelected.has(folder.folderID)) {
        folder.selected = true;
      }
    });

    // Prune vanished IDs so the action-bar count cannot show a ghost selection.
    const stillPresentIds = map(filter(this.folders, 'selected'), 'folderID');

    if (stillPresentIds.length) {
      this.panelConfig.selectedIds = stillPresentIds;
      this.updatePanelTitle();
    } else {
      this.clearFolderSelection();
    }
  };

  // One-shot guard set when a deliberate copy/move success clears the
  // selection, so the SSE-triggered, selection-PRESERVING refresh that the
  // same operation induces does not re-tick (resurrect) the just-cleared rows.
  // Honored + reset in refreshFolderData() / onMainTableRendered().
  private suppressSelectionRestore = false;

  constructor(
    private foldersDataService: FoldersDataService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private events: Events,
    private tableService: TableService,
    private popoverController: PopoverController,
    private folderPickerService: FolderPickerService,
    private loadingService: LoadingService,
    private foldersService: FoldersService,
    private translate: TranslateService,
    private toastService: ToastService,
    private nativeElementService: NativeElementService,
    private elementRef: ElementRef,
    private alertController: AlertController
  ) {}

  ngOnInit(): void {
    // Unique per folder type: Ionic keeps visited folder lists in the DOM, and DataTables
    // evicts any existing instance whose table has the same id when it initialises a new
    // one, so a shared id left the other page's table dead (CVX-6578).
    this.foldersTableID = `foldersTableID_${this.foldersConfig.config.type}`;
    this.panelConfig = this.foldersConfig.panelConfig;
    this.defaultPanelSubTitle = this.panelConfig?.subTitle;
    this.initPanelActions();
    this.updateFolderData();
    this.defineEmptyMessage();
    this.events.subscribe('ccs:foldersUpdate', this.foldersUpdateListener);
    this.filteredFoldersList = this.folders;
    this.initBulkImport();
  }

  ngOnDestroy(): void {
    this.events.unsubscribe('ccs:foldersUpdate', this.foldersUpdateListener);
  }

  public onClickAddFolder(): void {
    this.foldersService.openFolderView({
      folderType: this.foldersConfig.config.type,
      parentFolder: this.currentFolderId
    });
  }

  public onClickAddItem(): void {
    this.router.navigate([this.foldersConfig.config.addItemLink], {
      queryParams: { folderID: this.currentFolderId }
    });
  }

  public onClickBulkImport(): void {
    this.router.navigate([this.foldersConfig.config.bulkImport.link], {
      queryParams: { folderID: this.currentFolderId }
    });
  }

  /**
   * Resolve the bulk-import button's visibility once, here, rather than from the template:
   * templates must not call functions, and testCase() reaches into PermissionsService.
   * A config with no bulkImport block simply never shows the button.
   */
  private initBulkImport(): void {
    const bulkImport = this.foldersConfig.config?.bulkImport;
    this.showBulkImport = !!bulkImport && (!bulkImport.testCase || bulkImport.testCase());
  }

  public editColumns() {
    this.nativeElementService.getElementBy(this.elementRef.nativeElement, `#${this.dataTableComponent.tableId}_wrapper .buttons-colvis`).click();
    this.nativeElementService.getElementBy(this.elementRef.nativeElement, `#${this.dataTableComponent.tableId}`).scrollIntoView({ behavior: 'smooth' });
  }

  public updateFolderData(): void {
    this.panelConfig = this.foldersConfig.panelConfig;
    this.initPanelConfig();
    this.getFolderData();
    this.folderHeaderComponent?.initFilter();

    if (this.panelConfig?.selectionType === FolderPanelSelectionType.Items) {
      this.clearItemSelection();
    }
  }

  /**
   * Auto-refresh entry point (SSE ccs:assetsUpdate path via the asset page's
   * refreshData()). Refreshes the folder/data view WITHOUT clearing the current
   * selection — unlike updateFolderData(), which clears item selection for
   * explicit page re-entry / navigation.
   */
  public refreshFolderData(): void {
    // Snapshot the item selection BEFORE getFolderData() reloads the DataTable
    // data, so the SSE-triggered ccs:assetsUpdate auto-refresh preserves it.
    // A deliberate copy/move success just cleared the selection. Consume the
    // one-shot guard here so THIS refresh — induced by that same operation —
    // does not preserve the now-cleared selection. (onMainTableRendered honors
    // the same guard for the race where the SSE refresh opened its window
    // BEFORE the clear ran.)
    const intentionalClear = this.suppressSelectionRestore;
    if (intentionalClear) {
      this.suppressSelectionRestore = false;
    }
    const preservedItemIds = (!intentionalClear && this.panelConfig?.selectionType === FolderPanelSelectionType.Items)
      ? [...(this.panelConfig?.selectedIds || [])]
      : [];

    // Open a single coordinated preserving-refresh window. The guard is opened
    // here (BEFORE the redraw) so the synchronous empty onRowSelected fired by
    // TableService.showDataTable()'s reuse branch cannot zero the logical
    // selection, and is closed AFTER the render-complete re-tick. The suppress
    // flag (set on the freshly-built options below) is the explicit, refresh-
    // scoped guard against that same empty callback.
    if (preservedItemIds.length) {
      this.preservedItemIds = preservedItemIds;
      this.preservingRefresh = true;
      this.ignoreItemSelectionChanges = true;
    }

    this.getFolderData();

    if (this.preservingRefresh && this.mainDataTableOptions) {
      // getFolderData() rebuilt mainDataTableOptions; mark this next redraw as
      // selection-preserving so the reuse branch skips its empty callback. The
      // render-complete handler (onMainTableRendered) does the actual re-tick.
      this.mainDataTableOptions.suppressSelectionCallbackOnRedraw = true;
    }

    this.folderHeaderComponent?.initFilter();
  }

  /**
   * Render-complete handler bound to DataTableComponent's `(rendered)` output.
   * Fires once per redraw, AFTER the DataTables `draw` event. During a
   * selection-preserving refresh it re-applies the snapshotted item selection:
   * prunes IDs that no longer exist in the refreshed data (preventing a ghost
   * action-bar count), updates the logical selection, and re-ticks the surviving
   * DataTable rows by their stable rowId/assetID via the Select API. Because
   * rows are keyed by assetID (Task 1), the re-tick targets rows by stable id
   * and survives row-object replacement. The onRowSelected guard stays open
   * across the programmatic re-select so it does not double-count. After the
   * re-tick, the action-bar count (panelConfig.selectedIds/title) is reconciled
   * from the DataTable's ACTUAL surviving selection (rows({selected:true}) via
   * TableService.getSelectedRowIds) — never the stale pre-refresh component
   * value — so the count can never exceed the visible checkboxes (BUG B). Then
   * the coordinated preserving-refresh window is closed.
   */
  public onMainTableRendered(): void {
    if (!this.preservingRefresh) {
      // No preserving window opened for this redraw, but a deliberate copy/move
      // may still have armed the one-shot intentional-clear guard (e.g. its
      // induced refresh opened no preserving window). Consume it here so the
      // guard can never survive a render and leak into a later, unrelated
      // refresh that would then silently drop the user's selection.
      if (this.suppressSelectionRestore) {
        this.suppressSelectionRestore = false;
        this.clearAllSelections();
      }
      return;
    }

    const preservedItemIds = this.preservedItemIds;
    const rowKey = this.foldersConfig.tableConfig?.rowvaluefrom || 'assetID';
    const currentData = filter(this.foldersConfig.data, { folderID: this.currentFolderId }) as any[];
    const presentIds = filter(
      preservedItemIds,
      (id: number) => some(currentData, (row: any) => row[rowKey] === id)
    );

    // Close the coordinated preserving-refresh window once the re-tick is done.
    const closeWindow = () => {
      this.preservingRefresh = false;
      this.preservedItemIds = [];
      setTimeout(() => {
        this.ignoreItemSelectionChanges = false;
      });
    };

    // A deliberate copy/move cleared the selection after this preserving-refresh
    // window opened (the SSE refresh raced ahead of the clear). Honor that
    // intent: drop the snapshot and stay cleared rather than re-ticking the
    // surviving source rows.
    if (this.suppressSelectionRestore) {
      this.suppressSelectionRestore = false;
      this.clearAllSelections();
      closeWindow();
      return;
    }

    if (!presentIds.length) {
      this.clearAllSelections();
      closeWindow();
      return;
    }

    const table = this.dataTableComponent?.getTable();
    if (!table) {
      // No live DataTable to reconcile against; fall back to the present-in-data
      // intersection so the count still cannot resurrect deleted rows.
      this.panelConfig.selectedIds = presentIds;
      this.panelConfig.selectionType = FolderPanelSelectionType.Items;
      this.syncActiveButtons();
      this.updatePanelTitle();
      closeWindow();
      return;
    }

    // Re-tick the surviving rows by stable rowId; the guard is already open.
    (table as any)
      .rows((_idx: number, data: any) => includes(presentIds, data?.[rowKey]))
      .select();

    // BUG B fix: reconcile the count from the DataTable's ACTUAL surviving
    // selection rather than trusting the pre-refresh component value. After the
    // re-tick above, read back the live selected rows (rows({selected:true}))
    // via TableService.getSelectedRowIds and intersect with the prior selection.
    // This guarantees panelConfig.selectedIds can never exceed the rows that are
    // genuinely present-and-selected in the table, so the "{N} Selected" count
    // stays consistent with the visible checkboxes even if a row was dropped or
    // a re-tick failed to take.
    const tableAttr = this.foldersConfig.tableConfig || { rowvaluefrom: rowKey };
    const liveSelectedIds = map(
      this.tableService.getSelectedRowIds(table, tableAttr),
      (id) => +id
    );
    const survivingIds = filter(liveSelectedIds, (id: number) => includes(preservedItemIds, id));

    if (!survivingIds.length) {
      this.clearAllSelections();
      closeWindow();
      return;
    }

    this.panelConfig.selectedIds = survivingIds;
    this.panelConfig.selectionType = FolderPanelSelectionType.Items;
    this.syncActiveButtons();
    this.updatePanelTitle();

    closeWindow();
  }

  public customSearch(): void {
    const searchTerm = (this.searchWord || '').trim();

    // Scope to this component's table, not a global `#id` lookup (CVX-6578).
    const tableElement = this.dataTableComponent?.tableElement;
    if (tableElement?.querySelector('thead')) {
      const mainTable = $(tableElement).DataTable();

      if (mainTable.page.info().recordsTotal) {
        mainTable.search(searchTerm).draw(false);
        this.tableService.highlightSearchResult(tableElement, searchTerm);
      }
    }

    const normalizedSearchTerm = searchTerm.toUpperCase();

    this.filteredFoldersList = searchTerm.length
      ? filter(this.folders, (item: IFolder) => includes(item.title?.toUpperCase(), normalizedSearchTerm))
      : this.folders;
  }

  public clearSearch() {
    this.searchWord = '';
    this.customSearch();
  }

  public getSelectedFilter() {
    return this.foldersService?.getFilterData() || this.foldersConfig?.filter?.filterData;
  }

  public onFolderSelect() {
    const selectedIds = map(filter(this.folders, 'selected'), 'folderID');
    this.setActiveSelection(selectedIds, selectedIds.length ? FolderPanelSelectionType.Folders : null);
  }

  public setActiveSelection(ids: number[], type: FolderPanelSelectionType = FolderPanelSelectionType.Items): void {
    if (!ids.length || !type) {
      this.clearAllSelections();
      return;
    }

    if (includes([FolderPanelSelectionType.Items, FolderPanelSelectionType.Folders], type)) {
      if (type === FolderPanelSelectionType.Folders) {
        this.clearItemSelection(false);
      }
      
      this.panelConfig.selectedIds = ids;
      this.panelConfig.selectionType = type;

      this.syncActiveButtons();
      this.updatePanelTitle();
    } else {
      this.clearAllSelections();
    }
  }

  private initPanelActions(): void {
    this.initMoveAction();
    this.initCopyAction();
    this.initMoveFolderAction();
    this.initCopyFolderAction();
  }

  /**
   * Build the destination-tree nodes for an ITEM copy/move picker.
   *
   * FolderPickerService.getTreeNodes() yields only sub-folders (folderID > 0).
   * Items may also live at the root folder (folderID 0) UNLESS the config forbids
   * it (noItemsAtRoot — e.g. properties), so when root is allowed we wrap the
   * sub-folder nodes under an explicit selectable Top-Level node
   * (folderIdPrefix + '0'), mirroring the folder picker (getFolderMoveNodes).
   * The downstream pickedId parser resolves that node to folderID 0, which the
   * per-config copy/move request handlers already accept. When noItemsAtRoot is
   * set, root is NOT offered, preserving the prior behavior for those lists.
   */
  private buildItemPickerNodes(folderPickerConfig: FolderPickerConfig): TreeViewNode[] {
    const childNodes = this.folderPickerService.getTreeNodes(folderPickerConfig);
    if (this.foldersConfig.config?.noItemsAtRoot) {
      return childNodes;
    }
    return [{
      id: `${FolderPickerService.folderIdPrefix}0`,
      label: this.foldersConfig.tabTitle || 'SHARED.Top_Level',
      showFolderIcon: true,
      nodes: childNodes
    }];
  }

  private async moveItems() {
    const folderPickerConfig: FolderPickerConfig = {
      dataType: this.foldersConfig.config.type,
      entities: {},
      selectedIds: [],
      parentIds: [],
      getFolderId: (folder) => folder?.folderID
    };
    const formConfig = this.foldersConfig?.moveAction?.pageConfig?.formConfig;
    const element = await this.popoverController.create({
      component: ViewModalTreePopupComponent,
      animated: false,
      componentProps: {
        config: <ITreeViewModalConfig>{
          title: formConfig?.folderFieldTitle || 'SHARED.Folders',
          saveButtonName: formConfig?.config?.save || 'SHARED.Save',
          nodes: this.buildItemPickerNodes(folderPickerConfig)
        }
      }
    });

    await element.present();
    const event: OverlayEventDetail<TreeViewNodeIdType[]> = await element.onDidDismiss();

    // Guard on a real pick (mirrors copyItems): a dismissed picker yields no
    // pickedId, while picking the Top-Level node yields folderIdPrefix + '0'.
    // The old `if (+folderID)` test treated root (0) the same as a dismiss and
    // so silently dropped a legitimate move-to-root.
    const pickedId = event.data?.[0] as string | undefined;
    if (pickedId && pickedId.startsWith(FolderPickerService.folderIdPrefix)) {
      const [, folderID = 0] = split(pickedId, FolderPickerService.folderIdPrefix);

      await this.loadingService.enable();
      try {
        const result = await this.foldersConfig.moveAction.pageConfig.moveRequestHandler({ folderID: +folderID });

        if (result?.reqStatus === 'OK') {
          this.suppressSelectionRestore = true;
          this.clearAllSelections();
        } else {
          await this.toastService.showError('SHARED.Update_Failed');
        }
      } finally {
        await this.loadingService.disable();
      }
    }
  }

  private async copyItems() {
    // Snapshot the selected item ids at picker-open (mirrors copyFolders, which
    // snapshots selectedFolderIds before opening the picker). The destination picker
    // is an overlay, not a page navigation, so the underlying list keeps receiving
    // ccs:assetsUpdate SSE refreshes that can prune panelConfig.selectedIds while the
    // picker is open; passing the snapshot to the handler ensures the copy uses exactly
    // what the user had selected when they invoked Copy.
    const selectedItemIds = (this.panelConfig.selectedIds || []).map((id) => Number(id));
    const folderPickerConfig: FolderPickerConfig = {
      dataType: this.foldersConfig.config.type,
      entities: {},
      selectedIds: [],
      parentIds: [],
      getFolderId: (folder) => folder?.folderID
    };
    const element = await this.popoverController.create({
      component: ViewModalTreePopupComponent,
      animated: false,
      componentProps: {
        config: <ITreeViewModalConfig>{
          title: 'SHARED.Copy',
          saveButtonName: 'SHARED.Copy',
          nodes: this.buildItemPickerNodes(folderPickerConfig)
        }
      }
    });

    await element.present();
    const event: OverlayEventDetail<TreeViewNodeIdType[]> = await element.onDidDismiss();

    const pickedId = event.data?.[0] as string | undefined;
    if (pickedId && pickedId.startsWith(FolderPickerService.folderIdPrefix)) {
      // `0` is intentional here because the picker can target the root folder
      // (mirrors moveFolders/copyFolders — root is a valid copy destination).
      const [, targetFolderID = 0] = split(pickedId, FolderPickerService.folderIdPrefix);

      await this.loadingService.enable();
      try {
        const result = await this.foldersConfig.copyAction.copyRequestHandler(+targetFolderID, selectedItemIds);

        if (result?.reqStatus === 'OK') {
          this.suppressSelectionRestore = true;
          this.clearAllSelections();
          await this.toastService.show('SHARED.Copied');
        } else {
          await this.toastService.showError('SHARED.Update_Failed');
        }
      } finally {
        await this.loadingService.disable();
      }
    }
  }

  private async moveFolders() {
    const nodes = this.getFolderMoveNodes();

    if (!nodes.length) {
      return;
    }

    const selectedFolderIds = [...(this.panelConfig.selectedIds || [])];
    const element = await this.popoverController.create({
      component: ViewModalTreePopupComponent,
      animated: false,
      componentProps: {
        config: <ITreeViewModalConfig>{
          title: 'SHARED.Move_Folder',
          saveButtonName: 'SHARED.Move_Folder',
          disableSaveWhenEmpty: true,
          nodes
        }
      }
    });

    await element.present();
    const event: OverlayEventDetail<TreeViewNodeIdType[]> = await element.onDidDismiss();

    const pickedId = event.data?.[0] as string | undefined;
    if (pickedId && pickedId.startsWith(FolderPickerService.folderIdPrefix)) {
      // `0` is intentional here because the picker can target the root folder.
      const [, folderID = 0] = split(pickedId, FolderPickerService.folderIdPrefix);

      await this.loadingService.enable();
      this.ignoreFoldersUpdate = true;
      try {
        const results = await Promise.allSettled(map(selectedFolderIds, (selectedFolderId: number) => {
          return this.foldersDataService.updateFolder({
            folderID: selectedFolderId,
            parentID: +folderID
          });
        }));
        const failedFolderIds = selectedFolderIds.filter((selectedFolderId: number, index: number) => {
          const result = results[index];

          return result.status !== 'fulfilled' || result.value?.reqStatus !== 'OK';
        });

        if (failedFolderIds?.length) {
          this.panelConfig.selectedIds = failedFolderIds;
          this.panelConfig.selectionType = failedFolderIds.length ? FolderPanelSelectionType.Folders : null;
          this.syncActiveButtons();
          this.updatePanelTitle();

          await this.toastService.showError('SHARED.Update_Failed', undefined, {
            message: `${this.translate.instant('SHARED.Update_Failed')}: ${this.translate.instant('SHARED.Some_Folders_Failed_To_Move')}`
          });
        } else {
          this.clearAllSelections();
        }
      } finally {
        this.ignoreFoldersUpdate = false;
        this.foldersUpdateListener();
        await this.loadingService.disable();
      }
    }
  }

  private getFolderData() {
    let folders = this.foldersDataService.getFoldersByType(this.foldersConfig.config.type);
    folders = filter(folders, (folder) => {
      return this.foldersDataService.canAccessFolder(folder);
    });

    this.currentFolderId = +get(this.activatedRoute.snapshot.params, 'folderId') || 0;
    this.folders = orderBy(filter(folders, {parentID: this.currentFolderId}), [folder => folder.title.toLowerCase()], ['asc']);
    this.syncFolderSelection();
    this.dataTableOptions.data = filter(this.foldersConfig.data, {folderID: this.currentFolderId});
    const dataTableOptions = {...this.dataTableOptions, ...this.foldersConfig.tableConfig};
    dataTableOptions.onRowSelected = this.createRowSelectedHandler(dataTableOptions.onRowSelected);
    this.mainDataTableOptions = cloneDeep(dataTableOptions);
    this.searchModalTableOptions = cloneDeep(dataTableOptions);
    this.searchModalTableOptions.data = this.foldersConfig.data;
    this.allFoldersList = values(folders);

    if (this.mainDataTableOptions?.columns?.length) {
      this.tableDataReady = true;
    }
  }

  private initMoveAction() {
    if (this.foldersConfig?.moveAction?.panelTitle) {
      if (!some(this.itemButtons, {buttonText: this.foldersConfig?.moveAction?.panelTitle})) {
        this.itemButtons.push({
          buttonText: this.foldersConfig?.moveAction?.panelTitle,
          action: () => this.moveItems()
        });
      }
    }
  }

  private initCopyAction() {
    if (this.foldersConfig?.copyAction?.panelTitle) {
      if (!some(this.itemButtons, {buttonText: this.foldersConfig?.copyAction?.panelTitle})) {
        this.itemButtons.push({
          buttonText: this.foldersConfig?.copyAction?.panelTitle,
          action: () => this.copyItems()
        });
      }
    }
  }

  private initMoveFolderAction() {
    if (!some(this.folderButtons, {buttonText: 'SHARED.Move_Folder'})) {
      this.folderButtons.push({
        buttonText: 'SHARED.Move_Folder',
        action: () => this.moveFolders()
      });
    }
  }

  private initCopyFolderAction() {
    // Folder duplication (duplicateFolders) is asset-folder-only on the backend, so
    // gate the Copy Folder button on the page opting into copy support — the same
    // signal initCopyAction uses for the item Copy button. Without this gate the
    // button leaks onto every other FoldersListComponent consumer (properties,
    // content, external sources, gateways, notification rules, relays, …), where
    // clicking it would call duplicateFolders for a non-asset folder and surface an
    // error toast. Generalising copy to other types is tracked in CVX-6028.
    if (!this.foldersConfig?.copyAction?.panelTitle) {
      return;
    }
    if (!some(this.folderButtons, {buttonText: 'SHARED.Copy'})) {
      this.folderButtons.push({
        buttonText: 'SHARED.Copy',
        action: () => this.copyFolders()
      });
    }
  }

  private confirmCopyFolders(): Promise<boolean> {
    return new Promise((resolve) => {
      this.alertController.create({
        header: this.translate.instant('SHARED.Are_You_Sure'),
        message: this.translate.instant('SHARED.Copy_Folder_Confirm_Message'),
        buttons: [
          {
            text: this.translate.instant('SHARED.Cancel'),
            role: 'cancel',
            handler: () => resolve(false)
          },
          {
            text: this.translate.instant('SHARED.Copy'),
            handler: () => resolve(true)
          }
        ]
      }).then((alert) => {
        // Backdrop tap / Escape dismiss fires neither button handler, so resolve
        // false to avoid leaving copyFolders() awaiting a Promise that never
        // settles. No-op if a button handler already resolved (Promises settle once).
        alert.onDidDismiss().then(() => resolve(false));
        alert.present();
      });
    });
  }

  private async copyFolders() {
    const confirmed = await this.confirmCopyFolders();
    if (!confirmed) {
      return;
    }

    // getFolderMoveNodes() prunes every selected source folder and all of its
    // descendants via isFolderMoveTargetValid()/hasIn() applied across the whole
    // panelConfig.selectedIds set — i.e. the union of all selected subtrees.
    const nodes = this.getFolderMoveNodes();

    if (!nodes.length) {
      return;
    }

    const selectedFolderIds = [...(this.panelConfig.selectedIds || [])];
    const element = await this.popoverController.create({
      component: ViewModalTreePopupComponent,
      animated: false,
      componentProps: {
        config: <ITreeViewModalConfig>{
          title: 'SHARED.Copy',
          saveButtonName: 'SHARED.Copy',
          disableSaveWhenEmpty: true,
          nodes
        }
      }
    });

    await element.present();
    const event: OverlayEventDetail<TreeViewNodeIdType[]> = await element.onDidDismiss();

    const pickedId = event.data?.[0] as string | undefined;
    if (pickedId && pickedId.startsWith(FolderPickerService.folderIdPrefix)) {
      // `0` is intentional here because the picker can target the root folder.
      const [, targetParentID = 0] = split(pickedId, FolderPickerService.folderIdPrefix);

      await this.loadingService.enable();
      this.ignoreFoldersUpdate = true;
      try {
        const results = await Promise.allSettled(map(selectedFolderIds, (selectedFolderId: number) => {
          return this.foldersDataService.duplicateFolder(selectedFolderId, +targetParentID);
        }));
        const failedFolderIds = selectedFolderIds.filter((selectedFolderId: number, index: number) => {
          const result = results[index];

          return result.status !== 'fulfilled' || result.value?.reqStatus !== 'OK';
        });

        if (failedFolderIds?.length) {
          this.panelConfig.selectedIds = failedFolderIds;
          this.panelConfig.selectionType = failedFolderIds.length ? FolderPanelSelectionType.Folders : null;
          this.syncActiveButtons();
          this.updatePanelTitle();

          await this.toastService.showError('SHARED.Update_Failed', undefined, {
            message: `${this.translate.instant('SHARED.Update_Failed')}: ${this.translate.instant('SHARED.Some_Folders_Failed_To_Copy')}`
          });
        } else {
          this.clearAllSelections();
          await this.toastService.show('SHARED.Copied');
        }
      } finally {
        this.ignoreFoldersUpdate = false;
        this.foldersUpdateListener();
        await this.loadingService.disable();
      }
    }
  }

  private initPanelConfig() {
    this.panelConfig.selectedIds ||= [];
    this.panelConfig.buttons ||= [];
    this.syncActiveButtons();
  }

  private clearFolderSelection(resetState: boolean = true): void {
    each(this.folders, (folder: IFolder) => {
      folder.selected = false;
    });

    each(this.filteredFoldersList, (folder: IFolder) => {
      folder.selected = false;
    });

    if (resetState && this.panelConfig.selectionType === FolderPanelSelectionType.Folders) {
      // Clear the authoritative selected-ID set so the derived count reads 0;
      // updatePanelTitle() derives from panelConfig.selectedIds.
      this.panelConfig.selectedIds = [];
      this.panelConfig.selectionType = null;
      this.syncActiveButtons();
      this.updatePanelTitle();
    }
  }

  /**
   * Public entry point to clear the operational (item) selection. Folder-scoped
   * selection is intentionally local to a single folder's component instance:
   * folder-to-folder navigation mounts a fresh AssetsFoldersPage + FoldersListComponent
   * and leaves the previous folder's component alive in the Ionic nav stack with its
   * selection intact. The page calls this on ionViewWillLeave so that returning to a
   * folder shows no operational selection. Idempotent / no-op when nothing is selected
   * (clearItemSelection guards both the row deselect and the state reset).
   */
  public clearOperationalItemSelection(): void {
    this.clearItemSelection();
  }

  private clearItemSelection(resetState: boolean = true): void {
    const table = this.dataTableComponent?.getTable();
    const hasSelectedRows = !!table?.rows({selected: true})?.count();

    this.panelConfig.selectedIds = [];

    if (hasSelectedRows) {
      this.ignoreItemSelectionChanges = true;
      (table as any).rows().deselect();
      setTimeout(() => {
        this.ignoreItemSelectionChanges = false;
      });
    } else {
      this.ignoreItemSelectionChanges = false;
    }

    if (resetState && this.panelConfig.selectionType === FolderPanelSelectionType.Items) {
      this.panelConfig.selectionType = null;
      this.syncActiveButtons();
      this.updatePanelTitle();
    }
  }

  private clearAllSelections(): void {
    this.clearFolderSelection(false);
    this.clearItemSelection(false);
    this.panelConfig.selectedIds = [];
    this.panelConfig.selectionType = null;
    this.syncActiveButtons();
    this.updatePanelTitle();
  }

  /**
   * Single source of truth for the action-bar count. Derives the count from the
   * authoritative selected-ID set (panelConfig.selectedIds) rather than trusting
   * a count passed in by a caller, then writes the formatted title once into the
   * pre-computed panelConfig.title property (bound directly in the template — no
   * method call in folders-list.component.html). Every selection mutation path
   * (item re-tick, folder re-apply, clear, move/copy failure) sets
   * panelConfig.selectedIds first, then calls this to recompute. This makes a
   * stale count impossible: the title always reflects the live selection at the
   * moment it is written, so it can never show 0-while-checkboxes-checked nor a
   * prior count after an auto-refresh prunes vanished IDs.
   */
  private updatePanelTitle(): void {
    const count = this.panelConfig?.selectedIds?.length || 0;
    this.panelConfig.title = `${count} ${this.translate.instant('SHARED.Selected')}`;
  }

  private createRowSelectedHandler(originalHandler?: DataTableOptions['onRowSelected']) {
    return (currentRow, selectedIds, event) => {
      if (this.ignoreItemSelectionChanges) {
        return;
      }
      const normalizedSelectedIds = map(selectedIds, Number);

      if (normalizedSelectedIds.length) {
        this.clearFolderSelection(false);
      } else if (this.panelConfig.selectionType === FolderPanelSelectionType.Folders) {
        return;
      }

      this.panelConfig.selectedIds = normalizedSelectedIds;
      this.panelConfig.selectionType = normalizedSelectedIds.length ? FolderPanelSelectionType.Items : null;
      this.syncActiveButtons();
      this.updatePanelTitle();
      originalHandler?.(currentRow, normalizedSelectedIds, event);
    };
  }

  private syncActiveButtons(): void {
    if (this.panelConfig.selectionType === FolderPanelSelectionType.Items) {
      this.panelConfig.buttons = [...this.itemButtons];
    } else if (this.panelConfig.selectionType === FolderPanelSelectionType.Folders) {
      this.panelConfig.buttons = [...this.folderButtons];
    } else {
      this.panelConfig.buttons = [];
    }

    this.syncPanelSubTitle();
    this.syncActiveSelectionState();
  }

  private syncActiveSelectionState(): void {
    this.hasActiveSelection = includes(
      [FolderPanelSelectionType.Items, FolderPanelSelectionType.Folders],
      this.panelConfig?.selectionType
    ) && !!this.panelConfig?.selectedIds?.length;
  }

  private syncPanelSubTitle(): void {
    if (this.panelConfig.selectionType === FolderPanelSelectionType.Folders) {
      this.panelConfig.subTitle = 'SHARED.Folder(s)';
    } else {
      this.panelConfig.subTitle = this.defaultPanelSubTitle;
    }
  }

  private syncFolderSelection(): void {
    const selectedFolderIds = this.panelConfig?.selectionType === FolderPanelSelectionType.Folders
      ? this.panelConfig?.selectedIds || []
      : [];

    each(this.folders, (folder: IFolder) => {
      folder.selected = includes(selectedFolderIds, folder.folderID);
    });
  }

  private getFolderMoveNodes(): TreeViewNode[] {
    const folderTree = this.foldersDataService.getFolderTreeBy(this.foldersConfig.config.type);
    const rootFolders = filter(values(folderTree), (folder: any) => !folderTree[folder.parentID]);
    const childNodes = this.buildFolderMoveNodeBranch(rootFolders, folderTree);

    return [{
      id: `${FolderPickerService.folderIdPrefix}0`,
      label: this.foldersConfig.tabTitle || 'SHARED.Top_Level',
      showFolderIcon: true,
      nodes: childNodes
    }];
  }

  private buildFolderMoveNodeBranch(folders: IFolder[], folderTree): TreeViewNode[] {
    const accessibleFolders = filter(folders || [], (folder: IFolder) => this.foldersDataService.canAccessFolder(folder));
    return map(orderBy(accessibleFolders, [folder => folder.title.toLowerCase()], ['asc']), (folder: any) => {
      if (!this.isFolderMoveTargetValid(folder.folderID, folderTree)) {
        return null;
      }

      return {
        id: `${FolderPickerService.folderIdPrefix}${folder.folderID}`,
        label: folder.title,
        showFolderIcon: true,
        nodes: this.buildFolderMoveNodeBranch(folder.children || [], folderTree)
      };
    }).filter(Boolean);
  }

  private isFolderMoveTargetValid(targetFolderId: number, folderTree): boolean {
    return !some(this.panelConfig.selectedIds || [], (selectedFolderId: number) => {
      return this.foldersDataService.hasIn(selectedFolderId, targetFolderId, folderTree);
    });
  }

  private defineEmptyMessage() {
    if (this.currentFolderId === 0) {
      // Top level folder
      if (this.foldersConfig.config.noItemsAtRoot) {
        this.emptyMessage = 'PROPERTY.Add_Folder_To_Start';
      } else if (this.foldersConfig.config.noAddFolder) {
        this.emptyMessage = 'PROPERTY.Add_Item_To_Start';
      } else {
        this.emptyMessage = 'PROPERTY.Add_Folder_Or_Item_To_Start';
      }
    } else {
      // Child folder - always show generic empty message
      this.emptyMessage = 'PROPERTY.This_Folder_Is_Empty';
    }
  }
}
